import { fetchWithAuth } from "@/core/data/fetch-with-auth"
import { parseResponse, parseSuccessResponse } from "@/core/data/parse-response"
import type { SynthesisSessionRepository } from "../../domain/repositories/synthesis-session-repository"
import type {
  AskStreamEvent,
  ProcessingStep,
  ProcessingStepInfo,
} from "../../domain/entities/synthesis-session"
import type { SessionModel } from "../models/synthesis-session-model"
import {
  toCreateRequestBody,
  toSynthesisAskResult,
  toSynthesisSession,
  toSynthesisSessionSummaryList,
  toUpdateRequestBody,
} from "../mappers/synthesis-session-mapper"
import { parseSseStream } from "../utils/parse-sse-stream"

const parseSession = (data: unknown) => toSynthesisSession(data as SessionModel)

const PROCESSING_STEPS = new Set<ProcessingStep>([
  "find_documents",
  "read_content",
  "synthesize_documents",
  "aggregate",
  "synthesize_answer",
])

export const toAskStreamEvent = (
  event: string,
  data: unknown,
): AskStreamEvent | null => {
  switch (event) {
    case "intent_routing":
    case "planning":
    case "review":
    case "narrow_down_kbs":
    case "heartbeat":
      return { type: event }
    case "processing": {
      const step = (data as { step?: unknown } | null)?.step
      if (typeof step !== "string" || !PROCESSING_STEPS.has(step as ProcessingStep))
        return null
      return { type: "processing", step: step as ProcessingStep }
    }
    case "answer": {
      const delta = (data as { data?: unknown } | null)?.data
      if (typeof delta !== "string") return null
      return { type: "answer", delta }
    }
    case "done":
      return { type: "done", result: toSynthesisAskResult(data) }
    case "error": {
      const message = (data as { message?: unknown } | null)?.message
      return {
        type: "error",
        message: typeof message === "string" ? message : "Stream failed",
      }
    }
    default:
      return null
  }
}

const extractPreStreamError = async (res: Response): Promise<string> => {
  try {
    const body = (await res.json()) as {
      detail?: string
      message?: string
    } | null
    return (
      body?.detail ?? body?.message ?? `Request failed with status ${res.status}`
    )
  } catch {
    return `Request failed with status ${res.status}`
  }
}

export const httpSynthesisSessionRepository: SynthesisSessionRepository = {
  list: async () => {
    const res = await fetchWithAuth("/api/v1/sessions")
    const data = await parseResponse<unknown>(res)
    return toSynthesisSessionSummaryList(data)
  },

  getById: async (id) => {
    const res = await fetchWithAuth(`/api/v1/sessions/${encodeURIComponent(id)}`)
    const data = await parseResponse<unknown>(res)
    return parseSession(data)
  },

  create: async (input) => {
    const res = await fetchWithAuth("/api/v1/sessions", {
      method: "POST",
      body: JSON.stringify(toCreateRequestBody(input)),
    })
    const data = await parseResponse<unknown>(res)
    return parseSession(data)
  },

  askStream: async function* (sessionId, content, signal) {
    const res = await fetchWithAuth("/api/v1/messages", {
      method: "POST",
      headers: { Accept: "text/event-stream" },
      body: JSON.stringify({
        session_id: sessionId,
        message: { type: "text" as const, content },
        streaming: true,
      }),
      signal,
    })

    if (!res.ok || !res.body) {
      throw new Error(await extractPreStreamError(res))
    }

    for await (const frame of parseSseStream(res.body)) {
      const event = toAskStreamEvent(frame.event, frame.data)
      if (event) yield event
    }
  },

  update: async (id, input) => {
    const res = await fetchWithAuth(
      `/api/v1/sessions/${encodeURIComponent(id)}`,
      {
        method: "PUT",
        body: JSON.stringify(toUpdateRequestBody(input)),
      },
    )
    const data = await parseResponse<unknown>(res)
    return parseSession(data)
  },

  delete: async (id) => {
    const res = await fetchWithAuth(
      `/api/v1/sessions/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    )
    await parseSuccessResponse(res)
  },
}
